import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Grid,
  Divider,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// Marathi label mapping for form fields
const FIELD_LABELS = {
  // Identification fields
  district: 'जिल्हा',
  taluka: 'तालुका',
  talukaOther: 'इतर तालुका',
  village: 'गाव',
  ashaName: 'आशा कार्यकर्ता नाव',
  ashaCode: 'आशा कोड',
  anmName: 'ANM नाव',
  subCentre: 'उपकेंद्र',
  phcChc: 'प्राथमिक आरोग्य केंद्र',

  // Mother & Newborn fields
  motherName: 'आईचे नाव',
  motherAge: 'आईचे वय',
  motherAddress: 'पत्ता',
  mobileNumber: 'मोबाइल नंबर',
  deliveryDate: 'प्रसूती तारीख',
  deliveryPlace: 'प्रसूती ठिकाण',
  deliveryType: 'प्रसूतीचा प्रकार',
  childName: 'बालाचे नाव',
  gender: 'लिंग',
  birthWeight: 'जन्माचे वजन (किलो)',
  gestationalAge: 'गर्भावस्था काळ (आठवडे)',
  birthOrder: 'जन्म क्रम',
  dateOfBirth: 'जन्म तारीख',

  // Child Profile fields (HBYC)
  fatherName: 'वडिलांचे नाव',
  motherMobile: 'आईचा मोबाइल',
  houseNumber: 'घर क्रमांक',

  // Visit fields
  date: 'तारीख',
  visitDate: 'भेटीची तारीख',
  notes: 'टिपा',
  status: 'स्थिती',
  weight: 'वजन',
  height: 'उंची',
  temperature: 'तापमान',

  // Health Assessment
  healthIndicators: 'आरोग्य सूचक',
  dangerSigns: 'धोक्याची चिन्हे',
  referralNeeded: 'संदर्भ आवश्यक',
  referralPlace: 'संदर्भ ठिकाण',
  referralReason: 'संदर्भ कारण',

  // Counseling
  newbornCareTopics: 'नवजात काळजी विषय',
  motherCareTopics: 'आई काळजी विषय',
  hygieneTopics: 'स्वच्छता विषय',
  iecMaterialsProvided: 'IEC साहित्य प्रदान केले',
  familyParticipation: 'कुटुंब सहभाग',

  // Nutrition (HBYC)
  exclusiveBreastfeeding: 'केवळ स्तनपान',
  complementaryFeeding: 'पूरक आहार',
  feedingQuantity: 'आहार प्रमाण',
  feedingFrequency: 'आहार वारंवारता',

  // Health status (HBYC)
  diarrhea: 'अतिसार',
  orsGiven: 'ORS दिले',
  ifaSyrup: 'IFA सिरप',
  immunizationStatus: 'लसीकरण स्थिती',

  // Hygiene
  handwashing: 'हात धुणे',
  cleanWater: 'स्वच्छ पाणी',

  // Outcome
  finalStatus: 'अंतिम स्थिती',
  finalDevelopmentStatus: 'अंतिम विकास स्थिती',
  completionDate: 'पूर्णता तारीख',
  ashaSignature: 'आशा स्वाक्षरी',
  supervisorSignature: 'पर्यवेक्षक स्वाक्षरी',
  allVisitsCompleted: 'सर्व भेटी पूर्ण',
  underTreatment: 'उपचार सुरू',
  referred: 'संदर्भित',
  death: 'मृत्यू',
  deathDate: 'मृत्यू तारीख',
  deathReason: 'मृत्यूचे कारण',

  // Common fields
  remarks: 'शेरा',
  other: 'इतर',
  yes: 'होय',
  no: 'नाही',
};

function FormDetail() {
  const { formType, formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchFormDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, formId]);

  const fetchFormDetail = async () => {
    setLoading(true);
    try {
      let response;
      // Fetch specific form by ID using dedicated endpoints
      if (formType === 'hbnc') {
        response = await adminAPI.getHBNCForm(formId);
        setForm({ ...response.data, type: 'HBNC' });
      } else if (formType === 'hbyc') {
        response = await adminAPI.getHBYCForm(formId);
        setForm({ ...response.data, type: 'HBYC' });
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch form:', err);
      if (err.response?.status === 404) {
        setError('फॉर्म सापडला नाही');
      } else if (err.response?.status === 403) {
        setError('फॉर्म पाहण्याची परवानगी नाही');
      } else {
        setError('फॉर्म लोड करण्यात अयशस्वी');
      }
      setLoading(false);
    }
  };

  const getFieldLabel = (key) => {
    return FIELD_LABELS[key] || key;
  };

  const renderField = (label, value) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="textSecondary" fontWeight="bold">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body1">{value || '-'}</Typography>
      </Grid>
    </Grid>
  );

  const renderVisitCard = (visitKey, visitData, visitTitle) => {
    if (!visitData) return null;

    return (
      <Paper key={visitKey} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h6" gutterBottom color="primary">
          {visitTitle}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(visitData).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return null; // Skip nested objects
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                <Typography variant="body2" color="textSecondary">
                  {getFieldLabel(key)}
                </Typography>
                <Typography variant="body1">
                  {Array.isArray(value) ? value.join(', ') : value?.toString() || '-'}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  const renderHBNCContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'ओळख', key: 'identification' },
      { label: 'आई आणि बाळ', key: 'motherNewborn' },
      { label: 'भेटी', key: 'visits' },
      { label: 'आरोग्य तपासणी', key: 'healthAssessment' },
      { label: 'समुपदेशन', key: 'counseling' },
      { label: 'परिणाम', key: 'outcome' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      // Special handling for visits
      if (currentTab.key === 'visits') {
        const visitDays = [1, 3, 7, 14, 21, 28, 42];
        return (
          <Box>
            {visitDays.map(day => {
              const visitData = data[`visit_${day}`];
              return renderVisitCard(
                `visit_${day}`,
                visitData,
                `भेट ${day} (दिवस ${day})`
              );
            })}
          </Box>
        );
      }

      // Regular field display for other sections
      return (
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong> {subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(getFieldLabel(key), Array.isArray(value) ? value.join(', ') : value)}
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  const renderHBYCContent = () => {
    if (!form?.formData) return null;

    const tabs = [
      { label: 'बाळ प्रोफाइल', key: 'childProfile' },
      { label: 'भेटी (३-१५ महिने)', key: 'visits' },
      { label: 'परिणाम', key: 'outcome' },
    ];

    const renderTabContent = () => {
      const currentTab = tabs[activeTab];
      const data = form.formData[currentTab.key];

      if (!data) {
        return <Typography color="textSecondary">माहिती उपलब्ध नाही</Typography>;
      }

      // Special handling for visits
      if (currentTab.key === 'visits') {
        const visitMonths = [3, 6, 9, 12, 15];
        return (
          <Box>
            {visitMonths.map(month => {
              const visitData = data[`visit_${month}`];
              return renderVisitCard(
                `visit_${month}`,
                visitData,
                `भेट ${month} महिने`
              );
            })}
          </Box>
        );
      }

      // Regular field display for other sections
      return (
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return (
                <Grid item xs={12} key={key}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {getFieldLabel(key)}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    {Array.isArray(value) ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {value.map((item, idx) => (
                          <Chip key={idx} label={item} size="small" />
                        ))}
                      </Box>
                    ) : (
                      Object.entries(value).map(([subKey, subValue]) => (
                        <Typography key={subKey} variant="body2" sx={{ mb: 1 }}>
                          <strong>{getFieldLabel(subKey)}:</strong> {subValue?.toString() || '-'}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Grid>
              );
            }
            return (
              <Grid item xs={12} sm={6} key={key}>
                {renderField(getFieldLabel(key), Array.isArray(value) ? value.join(', ') : value)}
              </Grid>
            );
          })}
        </Grid>
      );
    };

    return (
      <>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>
        <Box>{renderTabContent()}</Box>
      </>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !form) {
    return (
      <Container>
        <Typography color="error">{error || 'फॉर्म सापडला नाही'}</Typography>
        <Button onClick={() => navigate('/admin/forms')} sx={{ mt: 2 }}>
          परत जा
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/forms')}
          sx={{ mr: 2 }}
        >
          परत
        </Button>
        <Typography variant="h4">
          {form.type} फॉर्म तपशील
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          मूलभूत माहिती
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {renderField('आशा कार्यकर्ता', form.ashaWorkerName)}
        {renderField('तारीख', new Date(form.createdAt).toLocaleString('mr-IN'))}
        {renderField('स्थिती', form.syncStatus === 'synced' ? 'समक्रमित' : 'प्रलंबित')}
      </Paper>

      <Paper sx={{ p: 3 }}>
        {form.type === 'HBNC' && renderHBNCContent()}
        {form.type === 'HBYC' && renderHBYCContent()}
      </Paper>
    </Container>
  );
}

export default FormDetail;
